import { ChangeEvent, useState, useEffect, ReactNode, useMemo, useRef, useCallback } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { TableColumn, TableAction } from '@/types';
import { Filter, Plus, Search, Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import DropdownActions from '@/components/ui/DropdownActions';
import Pagination from '@/components/ui/Pagination';
import { PaginatedResponse } from '@/services/apiClient.ts';
import { useLocation, useSearchParams } from 'react-router-dom';

type ColumnResizeState = {
  columnIndex: number;
  startX: number;
  startWidth: number;
};

export interface UseQueryHook<T> {
  data?: PaginatedResponse<T>;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  refetch: () => void;
}

export interface QueryParams {
  _page?: number;
  _size?: number;
  search?: string;
  _sort?: string;
  _condition?: 'and' | 'or';
  [key: string]: unknown;
}

export interface HeaderRenderProps<T extends Record<string, unknown>> {
  title: string;
  showTotalCount: boolean;
  totalCount: number;
  onCreate?: () => void;
  headerActions?: ReactNode;
  onFilter?: () => void;
  searchQuery: string;
  onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
  searchPlaceholder: string;
  isCompact: boolean;
  data?: PaginatedResponse<T>;
}

export interface DataTableWithQueryProps<T extends Record<string, unknown>> {
  columns: TableColumn<T>[];
  actions?: TableAction<T>[];
  useQueryHook: (params: QueryParams) => UseQueryHook<T>;
  onFilter?: () => void;
  onCreate?: () => void;
  headerActions?: ReactNode;
  headerRenderer?: (props: HeaderRenderProps<T>) => ReactNode;
  searchPlaceholder?: string;
  title?: string;
  itemsPerPage?: number;
  className?: string;
  initialFilters?: Record<string, unknown>;
  showTotalCount?: boolean;
  tableStyle?: 'default' | 'compact';
  enableRowSelection?: boolean;
  selectedRowIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  getRowId?: (item: T) => string;
  queryCondition?: 'and' | 'or';
  syncQueryString?: boolean;
  tableId?: string;
  actionsMobileIconOnly?: boolean;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

const getStoredPageSize = (storageKey: string, fallback: number) => {
  if (typeof window === 'undefined') return fallback;

  const raw = window.localStorage.getItem(storageKey);
  const parsed = Number(raw);

  if (PAGE_SIZE_OPTIONS.includes(parsed as (typeof PAGE_SIZE_OPTIONS)[number])) {
    return parsed;
  }

  return fallback;
};

function DataTableWithQuery<T extends Record<string, unknown>>({
  columns,
  actions = [],
  useQueryHook,
  onFilter,
  onCreate,
  headerActions,
  headerRenderer,
  searchPlaceholder = "Buscar...",
  title = "Datos",
  itemsPerPage = 25,
  className = "",
  initialFilters = {},
  showTotalCount = false,
  tableStyle = 'default',
  enableRowSelection = false,
  selectedRowIds = [],
  onSelectionChange,
  getRowId,
  queryCondition: queryConditionProp,
  syncQueryString = true,
  tableId,
  actionsMobileIconOnly = false,
}: DataTableWithQueryProps<T>) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tableInstanceKey = tableId || location.pathname;
  const pageSizeStorageKey = useMemo(
    () => `datatable-page-size:${tableInstanceKey}`,
    [tableInstanceKey]
  );
  const pageSizeSelectId = useMemo(
    () => `page-size-${encodeURIComponent(tableInstanceKey)}`,
    [tableInstanceKey]
  );
  const parsedPage = useMemo(() => {
    const pageParam = Number(searchParams.get('_page'));
    return Number.isInteger(pageParam) && pageParam >= 0 ? pageParam : 0;
  }, [searchParams]);
  const parsedSearch = searchParams.get('search') ?? '';
  const parsedSort = searchParams.get('_sort');
  const parsedSortDirection: 'asc' | 'desc' = parsedSort?.startsWith('-') ? 'desc' : 'asc';
  const parsedSortField = parsedSort
    ? parsedSort.replace(/^[+-]/, '').trim() || null
    : null;

  const [searchQuery, setSearchQuery] = useState(parsedSearch);
  const [currentPage, setCurrentPage] = useState(parsedPage);
  const [pageSize, setPageSize] = useState(() => getStoredPageSize(pageSizeStorageKey, itemsPerPage));
  const [debouncedSearch, setDebouncedSearch] = useState(parsedSearch);
  const [sortField, setSortField] = useState<string | null>(parsedSortField);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(parsedSortDirection);
  const [tableMaxHeight, setTableMaxHeight] = useState<number | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<number, number>>({});
  const selectionHeaderRef = useRef<HTMLInputElement | null>(null);
  const tableHeaderRef = useRef<HTMLTableSectionElement | null>(null);
  const tableColumnRefs = useRef<Array<HTMLTableCellElement | null>>([]);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const contentContainerRef = useRef<HTMLElement | null>(null);
  const hasMountedRef = useRef(false);
  const resizingColumnRef = useRef<ColumnResizeState | null>(null);
  const bodyCursorRef = useRef('');
  const bodyUserSelectRef = useRef('');
  const isCompact = tableStyle === 'compact';
  const minColumnWidth = isCompact ? 80 : 120;
  const hasResizedColumns = Object.keys(columnWidths).length > 0;
  const TABLE_BOTTOM_PADDING_PX = 16; // Tailwind pb-4
  const FOOTER_FALLBACK_HEIGHT_PX = isCompact ? 72 : 80;
  const TABLE_HEIGHT_SAFETY_PX = 1;
  const columnSizingKey = useMemo(
    () => columns.map((column) => `${String(column.key)}:${column.header}`).join('|'),
    [columns]
  );

  const resolveRowId = useMemo(() => {
    if (getRowId) return getRowId;
    return (item: T) => String((item as { id?: unknown }).id ?? '');
  }, [getRowId]);

  const selectionEnabled = enableRowSelection && Boolean(onSelectionChange);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (hasMountedRef.current) {
        setCurrentPage(0);
      } else {
        hasMountedRef.current = true;
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!syncQueryString) return;

    const nextParams = new URLSearchParams(searchParams);

    if (currentPage > 0) {
      nextParams.set('_page', String(currentPage));
    } else {
      nextParams.delete('_page');
    }

    if (pageSize > 0) {
      nextParams.set('_size', String(pageSize));
    } else {
      nextParams.delete('_size');
    }

    if (debouncedSearch.trim()) {
      nextParams.set('search', debouncedSearch.trim());
    } else {
      nextParams.delete('search');
    }

    if (sortField) {
      nextParams.set('_sort', `${sortDirection === 'asc' ? '+' : '-'}${sortField}`);
    } else {
      nextParams.delete('_sort');
    }

    const currentParamsString = searchParams.toString();
    const nextParamsString = nextParams.toString();
    if (nextParamsString !== currentParamsString) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    currentPage,
    debouncedSearch,
    pageSize,
    searchParams,
    setSearchParams,
    sortDirection,
    sortField,
    syncQueryString,
  ]);

  const queryCondition = queryConditionProp ?? 'or';

  const queryParams: QueryParams = {
    _page: currentPage,
    _size: pageSize,
    search: debouncedSearch || undefined,
    _sort: sortField ? `${sortDirection === 'asc' ? '+' : '-'}${sortField}` : undefined,
    _condition: queryConditionProp ? queryCondition : undefined,
    ...initialFilters,
  };

  const { data, isLoading, isError, error, refetch } = useQueryHook(queryParams);

  const pageRowIds = useMemo(() => {
    if (!data?.content) return [];
    return data.content
      .map((item) => resolveRowId(item))
      .filter((id) => id);
  }, [data, resolveRowId]);

  const selectedSet = useMemo(() => new Set(selectedRowIds), [selectedRowIds]);
  const selectedOnPageCount = useMemo(
    () => pageRowIds.filter((id) => selectedSet.has(id)).length,
    [pageRowIds, selectedSet]
  );
  const allOnPageSelected = pageRowIds.length > 0 && selectedOnPageCount === pageRowIds.length;
  const someOnPageSelected = selectedOnPageCount > 0 && !allOnPageSelected;

  useEffect(() => {
    if (selectionHeaderRef.current) {
      selectionHeaderRef.current.indeterminate = someOnPageSelected;
    }
  }, [someOnPageSelected]);

  useEffect(() => {
    setPageSize(getStoredPageSize(pageSizeStorageKey, itemsPerPage));
  }, [pageSizeStorageKey, itemsPerPage]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(pageSizeStorageKey, String(pageSize));
  }, [pageSizeStorageKey, pageSize]);

  const updateTableMaxHeight = useCallback(() => {
    if (!tableHeaderRef.current) return;

    const headerTop = tableHeaderRef.current.getBoundingClientRect().top;
    const contentBottom = contentContainerRef.current
      ? contentContainerRef.current.getBoundingClientRect().bottom
      : window.innerHeight;
    const hasFooter = Boolean(data && data.totalElements > 0);
    const footerHeight = hasFooter
      ? (footerRef.current?.getBoundingClientRect().height ?? FOOTER_FALLBACK_HEIGHT_PX)
      : 0;
    const nextHeight = Math.max(
      180,
      Math.floor(contentBottom - headerTop - footerHeight - TABLE_BOTTOM_PADDING_PX - TABLE_HEIGHT_SAFETY_PX)
    );

    setTableMaxHeight((prev) => (prev === nextHeight ? prev : nextHeight));
  }, [data, FOOTER_FALLBACK_HEIGHT_PX]);

  useEffect(() => {
    if (tableHeaderRef.current) {
      contentContainerRef.current = tableHeaderRef.current.closest('.app-content') as HTMLElement | null;
    }
    updateTableMaxHeight();
  }, [
    updateTableMaxHeight,
    isCompact,
    headerRenderer,
    data?.content?.length,
    data?.totalElements,
    selectionEnabled,
    columns.length,
    actions.length,
  ]);

  useEffect(() => {
    window.addEventListener('resize', updateTableMaxHeight);
    return () => {
      window.removeEventListener('resize', updateTableMaxHeight);
    };
  }, [updateTableMaxHeight]);

  useEffect(() => {
    setColumnWidths({});
  }, [columnSizingKey]);

  useEffect(() => {
    const stopResizing = () => {
      if (!resizingColumnRef.current) return;

      resizingColumnRef.current = null;
      document.body.style.cursor = bodyCursorRef.current;
      document.body.style.userSelect = bodyUserSelectRef.current;
    };

    const handlePointerMove = (event: PointerEvent) => {
      const resizeState = resizingColumnRef.current;
      if (!resizeState) return;

      const nextWidth = Math.max(
        minColumnWidth,
        resizeState.startWidth + event.clientX - resizeState.startX
      );

      setColumnWidths((prev) => (
        prev[resizeState.columnIndex] === nextWidth
          ? prev
          : { ...prev, [resizeState.columnIndex]: nextWidth }
      ));
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResizing);
    window.addEventListener('pointercancel', stopResizing);

    return () => {
      stopResizing();
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResizing);
      window.removeEventListener('pointercancel', stopResizing);
    };
  }, [minColumnWidth]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      updateTableMaxHeight();
    });

    if (tableHeaderRef.current) observer.observe(tableHeaderRef.current);
    if (footerRef.current) observer.observe(footerRef.current);
    if (contentContainerRef.current) observer.observe(contentContainerRef.current);

    return () => observer.disconnect();
  }, [updateTableMaxHeight, data?.totalElements]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRefresh = () => {
    refetch();
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(0);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(0); // Reset to first page when sorting
  };

  const handleColumnResizeStart = (
    event: ReactPointerEvent<HTMLButtonElement>,
    columnIndex: number
  ) => {
    if (event.button !== 0) return;

    const columnWidth = tableColumnRefs.current[columnIndex]?.getBoundingClientRect().width ?? minColumnWidth;

    event.preventDefault();
    event.stopPropagation();
    resizingColumnRef.current = {
      columnIndex,
      startX: event.clientX,
      startWidth: columnWidth,
    };

    bodyCursorRef.current = document.body.style.cursor;
    bodyUserSelectRef.current = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleColumnResizeKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    columnIndex: number
  ) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    event.stopPropagation();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const currentWidth = columnWidths[columnIndex]
      ?? tableColumnRefs.current[columnIndex]?.getBoundingClientRect().width
      ?? minColumnWidth;

    setColumnWidths((prev) => ({
      ...prev,
      [columnIndex]: Math.max(minColumnWidth, currentWidth + direction * 16),
    }));
  };

  const resetColumnWidth = (columnIndex: number) => {
    setColumnWidths((prev) => {
      if (!(columnIndex in prev)) return prev;

      const next = { ...prev };
      delete next[columnIndex];
      return next;
    });
  };

  const getTextAlignClass = (align?: 'left' | 'center' | 'right') => {
    if (align === 'center') return 'text-center';
    if (align === 'right') return 'text-right';
    return 'text-left';
  };

  const getJustifyClass = (align?: 'left' | 'center' | 'right') => {
    if (align === 'center') return 'justify-center';
    if (align === 'right') return 'justify-end';
    return 'justify-start';
  };

  const toggleRowSelection = (rowId: string, checked: boolean) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedRowIds);
    if (checked) {
      next.add(rowId);
    } else {
      next.delete(rowId);
    }
    onSelectionChange(Array.from(next));
  };

  const toggleAllOnPage = (checked: boolean) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedRowIds);
    if (checked) {
      pageRowIds.forEach((id) => next.add(id));
    } else {
      pageRowIds.forEach((id) => next.delete(id));
    }
    onSelectionChange(Array.from(next));
  };

  if (isError) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error al cargar los datos</h3>
          <p className="text-gray-500 mb-4">{error?.message || 'Ocurrió un error inesperado'}</p>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const columnCount = columns.length + (actions.length > 0 ? 1 : 0) + (selectionEnabled ? 1 : 0);

  return (
    <div className={`bg-white shadow ${className}`}>
      {headerRenderer ? (
        <div className="relative z-30">
          {headerRenderer({
            title,
            showTotalCount,
            totalCount: data?.totalElements ?? 0,
            onCreate,
            headerActions,
            onFilter,
            searchQuery,
            onSearchChange: handleSearch,
            searchPlaceholder,
            isCompact,
            data,
          })}
        </div>
      ) : (
        <div className={`${isCompact ? 'px-4 py-3' : 'px-6 py-4'} border-b border-gray-200 relative z-30`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <h3 className="text-lg font-medium text-gray-900">{title}</h3>
              {showTotalCount && data && (
                <span
                  className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-md font-bold text-white ">
                  {data.totalElements}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {onCreate && (
                <button
                  onClick={onCreate}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <Plus size={16} className="mr-2" />
                  Crear
                </button>
              )}
              {headerActions}
            </div>
          </div>
          <div className={`${isCompact ? 'mt-3' : 'mt-4'} flex items-center space-x-4`}>
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={handleSearch}
                className={`${isCompact ? 'py-1 text-base' : 'py-2'} w-full pl-10 pr-4 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500`}
              />
            </div>
            {onFilter && (
              <button
                onClick={onFilter}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Filter size={16} className="mr-2" />
                Filtros
              </button>
            )}
          </div>
        </div>
      )}
      <div className="relative z-20 overflow-visible">
        <div
          className="overflow-auto"
          style={tableMaxHeight ? { maxHeight: `${tableMaxHeight}px` } : undefined}
        >
          <table className={`min-w-full ${hasResizedColumns ? 'table-fixed' : ''}`}>
            <colgroup>
              {selectionEnabled && <col />}
              {columns.map((_, index) => (
                <col
                  key={index}
                  style={columnWidths[index] ? { width: `${columnWidths[index]}px` } : undefined}
                />
              ))}
              {actions.length > 0 && <col />}
            </colgroup>
            <thead ref={tableHeaderRef} className="bg-gray-50">
              <tr>
                {selectionEnabled && (
                  <th className={`${isCompact ? 'px-3 py-1' : 'px-6 py-3'} sticky top-0 z-20 border-b border-gray-200 bg-gray-50 shadow-[inset_0_-1px_0_0_rgb(229,231,235)] text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10`}>
                    <input
                      ref={selectionHeaderRef}
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={(event) => toggleAllOnPage(event.target.checked)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                  </th>
                )}
                {columns.map((column, index) => (
                  <th
                    key={index}
                    ref={(element) => {
                      tableColumnRefs.current[index] = element;
                    }}
                    className={`${isCompact ? 'px-3 py-1 text-[11px]' : 'px-6 py-3 text-xs'} sticky top-0 z-20 border-b border-gray-200 bg-gray-50 shadow-[inset_0_-1px_0_0_rgb(229,231,235)] ${getTextAlignClass(column.align)} font-medium text-gray-500 uppercase tracking-wider ${column.className || ''} ${column.sortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''}`}
                    onClick={column.sortable ? () => handleSort(column.key as string) : undefined}
                  >
                    <div className={`flex items-center ${getJustifyClass(column.align)} overflow-hidden`}>
                      <span>{column.header}</span>
                      {column.sortable && (
                        <div className="ml-2 flex flex-col">
                          {sortField === column.key ? (
                            sortDirection === 'asc' ? (
                              <ChevronUp size={14} className="text-gray-700" />
                            ) : (
                              <ChevronDown size={14} className="text-gray-700" />
                            )
                          ) : (
                            <div className="w-3.5 h-3.5" />
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      aria-label={`Redimensionar columna ${column.header}`}
                      className="absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none select-none bg-transparent hover:bg-indigo-300/70 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                      onClick={(event) => event.stopPropagation()}
                      onPointerDown={(event) => handleColumnResizeStart(event, index)}
                      onKeyDown={(event) => handleColumnResizeKeyDown(event, index)}
                      onDoubleClick={() => resetColumnWidth(index)}
                    >
                      <span className="sr-only">Redimensionar</span>
                    </button>
                  </th>
                ))}
                {actions.length > 0 && (
                  <th className={`${isCompact ? 'px-3 py-1 text-[11px]' : 'px-6 py-3 text-xs'} sticky top-0 z-20 border-b border-gray-200 bg-gray-50 shadow-[inset_0_-1px_0_0_rgb(229,231,235)] text-right font-medium text-gray-500 uppercase tracking-wider w-10`}>
                    Opciones
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={columnCount} className={`${isCompact ? 'px-3 py-10' : 'px-6 py-12'} text-center`}>
                    <div className="flex items-center justify-center">
                      <Loader2 size={24} className="animate-spin text-gray-400 mr-2" />
                      <span className="text-gray-500">Cargando...</span>
                    </div>
                  </td>
                </tr>
              ) : !data?.content || data.content.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className={`${isCompact ? 'px-3 py-10' : 'px-6 py-12'} text-center`}>
                    <div className="text-gray-500">
                      {searchQuery ? 'No se encontraron resultados para tu búsqueda.' : 'No hay datos disponibles.'}
                    </div>
                  </td>
                </tr>
              ) : (
                data.content.map((item: T, index: number) => (
                  <tr key={index} className="hover:bg-gray-50">
                    {selectionEnabled && (
                      <td className={`${isCompact ? 'px-3 py-1' : 'px-6 py-4'} whitespace-nowrap`}>
                        {resolveRowId(item) ? (
                          <input
                            type="checkbox"
                            checked={selectedSet.has(resolveRowId(item))}
                            onChange={(event) => toggleRowSelection(resolveRowId(item), event.target.checked)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    )}
                    {columns.map((column, colIndex) => (
                      <td key={colIndex} className={`${isCompact ? 'px-3 py-1 text-sm' : 'px-6 py-4'} ${getTextAlignClass(column.align)} whitespace-nowrap ${column.className || ''}`}>
                        {column.accessor
                          ? column.accessor(item, index)
                          : String(item[column.key] || '')
                        }
                      </td>
                    ))}
                    {actions.length > 0 && (
                      <td className={`${isCompact ? 'px-3 py-1' : 'px-6 py-4'} whitespace-nowrap text-right text-sm font-medium`}>
                        <DropdownActions actions={actions} item={item} mobileIconOnly={actionsMobileIconOnly} />
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data && data.totalElements > 0 && (
        <div ref={footerRef} className="relative z-10">
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            onPageChange={handlePageChange}
            size={pageSize}
            totalElements={data.totalElements}
            controlsPrefix={(
              <div className="flex items-center gap-2 mr-2">
                <label htmlFor={`${pageSizeSelectId}-desktop`} className="hidden whitespace-nowrap text-sm text-gray-500 sm:inline">
                  Mostrar
                </label>
                <select
                  id={`${pageSizeSelectId}-mobile`}
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:hidden"
                >
                  {PAGE_SIZE_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {value}/pág.
                    </option>
                  ))}
                </select>
                <select
                  id={`${pageSizeSelectId}-desktop`}
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="hidden rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:inline-block"
                >
                  {PAGE_SIZE_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
                <span className="hidden whitespace-nowrap text-sm text-gray-500 sm:inline">por página</span>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}

export default DataTableWithQuery;
